const BASE = '/api';

function getToken() {
  return localStorage.getItem('annotate_token');
}

function getLang() {
  return localStorage.getItem('annotate_lang') || 'en';
}

function authHeaders() {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function apiFetch(url, opts = {}) {
  const res = await fetch(url, {
    ...opts,
    headers: { 'X-Language': getLang(), ...authHeaders(), ...(opts.headers || {}) },
  });
  if (res.status === 401) {
    const hadToken = !!getToken();
    localStorage.removeItem('annotate_token');
    if (hadToken) window.location.reload();
    throw new Error('Não autenticado');
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || `Erro ${res.status}`);
  }
  return res.json();
}

// ── Auth ─────────────────────────────────────────────────────────────────────

export async function fetchChallenge() {
  const r = await fetch(`${BASE}/auth/challenge`);
  if (!r.ok) throw new Error('Erro ao obter challenge');
  return r.json();
}

export async function login(username, password, challenge, nonce, pow_hash) {
  const r = await fetch(`${BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password, challenge, nonce, pow_hash, honeypot: '' }),
  });
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    throw new Error(body.detail || 'Erro ao fazer login');
  }
  const data = await r.json();
  localStorage.setItem('annotate_token', data.token);
  return data;
}

export async function logout() {
  await fetch(`${BASE}/auth/logout`, {
    method: 'POST',
    headers: authHeaders(),
  }).catch(() => {});
  localStorage.removeItem('annotate_token');
}

export async function me() {
  return apiFetch(`${BASE}/auth/me`);
}

// ── Tags ──────────────────────────────────────────────────────────────────────

export async function fetchTags() {
  return apiFetch(`${BASE}/tags`);
}

export async function createTag(name, color) {
  return apiFetch(`${BASE}/tags`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, color }),
  });
}

export async function updateTag(id, data) {
  return apiFetch(`${BASE}/tags/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteTag(id) {
  return apiFetch(`${BASE}/tags/${id}`, { method: 'DELETE' });
}

// ── custom icons (reusable link icons) ───────────────────────────────────────

export async function fetchIcons() {
  return apiFetch(`${BASE}/icons`);
}

// Create an emoji/HTML icon (value) or an image-URL icon (url → <img> value).
export async function createIcon({ name, value, url }) {
  return apiFetch(`${BASE}/icons`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, value, url }),
  });
}

export async function uploadIcon(file, name) {
  const fd = new FormData();
  fd.append('file', file);
  if (name) fd.append('name', name);
  return apiFetch(`${BASE}/icons/upload`, { method: 'POST', body: fd });
}

export async function updateIcon(id, data) {
  return apiFetch(`${BASE}/icons/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteIcon(id) {
  return apiFetch(`${BASE}/icons/${id}`, { method: 'DELETE' });
}

// ── Images ────────────────────────────────────────────────────────────────────

export async function fetchImages(tagIds = []) {
  const qs = tagIds.length ? `?tag_ids=${tagIds.join(',')}` : '';
  return apiFetch(`${BASE}/images${qs}`);
}

export async function fetchImage(id) {
  return apiFetch(`${BASE}/images/${id}`);
}

export async function uploadImage(file, name, tagIds = []) {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('name', name || file.name);
  if (tagIds.length) fd.append('tag_ids', tagIds.join(','));
  return apiFetch(`${BASE}/images`, { method: 'POST', body: fd });
}

// exportImages downloads a tar.gz of the given image ids (empty → all) in the
// import-images archive format. Returns the response Blob.
export async function exportImages(ids) {
  const res = await fetch(`${BASE}/export-images`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Language': getLang(), ...authHeaders() },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) throw new Error(`Erro ${res.status}`);
  return res.blob();
}

export async function importImages(file) {
  const fd = new FormData();
  fd.append('file', file);
  return apiFetch(`${BASE}/import-images`, { method: 'POST', body: fd });
}

export async function updateImage(id, data) {
  return apiFetch(`${BASE}/images/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteImage(id) {
  return apiFetch(`${BASE}/images/${id}`, { method: 'DELETE' });
}

export async function saveAnnotations(id, annotations) {
  return apiFetch(`${BASE}/images/${id}/annotations`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ annotations }),
  });
}

export function imageUrl(filename) {
  return `/uploads/${filename}`;
}

// ── Annotation Images ──────────────────────────────────────────────────────────

export async function listAnnotationImages(annId) {
  return apiFetch(`${BASE}/annotations/${annId}/images`);
}

export async function createAnnotationImage(annId, url, title, storeOnDisk, description, link) {
  return apiFetch(`${BASE}/annotations/${annId}/images`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url, title: title || '', store_on_disk: !!storeOnDisk, description: description || '', link: link || '' }),
  });
}

export async function importImagesFromWeb(pageUrls, storeOnDisk) {
  return apiFetch(`${BASE}/import-images-from-web`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ page_urls: pageUrls, store_on_disk: !!storeOnDisk }),
  });
}

export async function uploadAnnotationImage(annId, file, title, description, link) {
  const fd = new FormData();
  fd.append('file', file);
  if (title) fd.append('title', title);
  if (description) fd.append('description', description);
  if (link) fd.append('link', link);
  return apiFetch(`${BASE}/annotations/${annId}/images/upload`, { method: 'POST', body: fd });
}

export async function updateAnnotationImage(annId, imgId, data) {
  return apiFetch(`${BASE}/annotations/${annId}/images/${imgId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteAnnotationImage(annId, imgId) {
  return apiFetch(`${BASE}/annotations/${annId}/images/${imgId}`, { method: 'DELETE' });
}

export function exportAnnotationsJSON(image) {
  const payload = {
    image: {
      id: image.id,
      name: image.name,
      filename: image.filename,
      original_width: image.original_width,
      original_height: image.original_height,
      tags: (image.tags || []).map(t => ({ id: t.id, name: t.name, color: t.color })),
    },
    annotations: image.annotations || [],
    exported_at: new Date().toISOString(),
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${image.name.replace(/[^a-z0-9_-]/gi, '_')}_annotations.json`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Users ─────────────────────────────────────────────────────────────────────

export async function fetchUsers() {
  return apiFetch(`${BASE}/users`);
}

export async function createUser(username, isAdmin) {
  return apiFetch(`${BASE}/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, is_admin: isAdmin }),
  });
}

export async function updateUser(id, data) {
  return apiFetch(`${BASE}/users/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteUser(id) {
  return apiFetch(`${BASE}/users/${id}`, { method: 'DELETE' });
}

export async function resetUserPassword(id) {
  return apiFetch(`${BASE}/users/${id}/reset-password`, { method: 'POST' });
}

export async function fetchUserSessions(id) {
  return apiFetch(`${BASE}/users/${id}/sessions`);
}

export async function terminateAllSessions(id) {
  return apiFetch(`${BASE}/users/${id}/sessions`, { method: 'DELETE' });
}

export async function terminateSession(userId, sessionId) {
  return apiFetch(`${BASE}/users/${userId}/sessions/${sessionId}`, { method: 'DELETE' });
}

// ── Languages & Translations ──────────────────────────────────────────────────

export async function fetchLanguages() {
  return apiFetch(`${BASE}/languages`);
}

export async function createLanguage(code, name) {
  return apiFetch(`${BASE}/languages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, name }),
  });
}

export async function updateLanguage(id, name) {
  return apiFetch(`${BASE}/languages/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
}

export async function deleteLanguage(id) {
  return apiFetch(`${BASE}/languages/${id}`, { method: 'DELETE' });
}

export async function fetchLanguageTranslations(id) {
  return apiFetch(`${BASE}/languages/${id}/translations`);
}

export async function saveLanguageTranslations(id, frontend, backend) {
  return apiFetch(`${BASE}/languages/${id}/translations`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ frontend, backend }),
  });
}

export async function fetchTranslationDefaults() {
  return apiFetch(`${BASE}/translations/defaults`);
}

// ── Version ───────────────────────────────────────────────────────────────────

export async function fetchVersion() {
  return apiFetch(`${BASE}/version`);
}
